// MELDINGSUTKASTET — fra en sekvens til en melding SKADE kan lese 1:1.
//
// ══ HVILKEN KONTRAKT, OG HVORFOR IKKE EN NY ═════════════════════════════════
// Det ble IKKE laget et nytt meldingsformat. To finnes allerede, og begge brukes
// — til to ulike ting:
//
//   * `report.ts` sin `CombatReport` med `type: 'MELDING'` er DET SOM SENDES.
//     Den er wire-formatet hubben og COP allerede deler, med `lat`/`lon`,
//     `author`, `status` og en dialogtråd. Feltnøklene er hentet fra
//     `REPORT_FIELDS.MELDING` og ikke skrevet på nytt, så en melding fra ULL
//     havner i COP som en helt vanlig stridsmelding — ingen egen ULL-visning, og
//     ingen «ULL-melding» noen må lære seg.
//
//   * `stridsmelding.ts` sin `Stridsmelding` + `formatStridsmelding()` er
//     KLARTEKSTFORMEN. Den finnes fordi den viktigste transporten for et
//     jegerlag ikke er en datalenke: det er en stemme på en radio. Åtte linjer
//     DTG/FRA/TIL/HVEM/HVA/HVOR/NÅR/HVORLEDES er nettopp det man leser opp, og
//     `stridsEmphasis()` gir doktrinens store bokstaver på HIMMELRETNINGER og
//     ordet «IKKE» uten at jegeren må huske det midt i en opplesning.
//
// Klarteksten sendes MED i `UllKommando.melding.klartekst`, slik at SKADE og
// jegeren har presis samme ordlyd. En omformatering i mottak er en divergens
// ingen oppdager før to folk leser ulikt over radio.
//
// SALUTE ble vurdert og forkastet: den erklærer «fiendtlig observasjon», og en
// UGS-utløsning er ikke det. Kontrakten setter selv tilhørighet til `unknown` for
// en utløsning, med begrunnelsen at det kan være en elg, en bærplukker eller egen
// patrulje som gikk feil. En SALUTE fra en rystelse i bakken ville vært en
// automatisert fiendeerklæring.
//
// ══ HVA UTKASTET ALDRI GJØR ════════════════════════════════════════════════
// Det gjetter ikke. Hvert felt er bygget av tall som står i kontraktens eget svar,
// og `grunnlag` lister hva som ble brukt slik at jegeren kan se at feltene ikke er
// magi. Der kontrakten ikke kan svare (retning ubestemt), står det i meldingen at
// den er ubestemt — ikke en utfylt retning som ser like autoritativ ut.
import {
  CombatReportSchema,
  StridsmeldingSchema,
  buildDtg,
  dekningsradiusM,
  formatStridsmelding,
  latLonToMgrs,
  posisjonUsikkerhetM,
  sensorPos,
  type CombatReport,
  type Stridsmelding,
  type StridsSone,
  type UgsSensor,
} from '@skade/contracts';
import { fartsutsagn, formaterAntall, formaterVarighet, GRUNNLAG_NAVN, MALKLASSE_NAVN } from './format.js';
import { retningsutsagn, type Kandidat } from './sekvens.js';

/** Feltnøklene er `REPORT_FIELDS.MELDING`. Formen speiles her for typesikkerhet. */
export interface Meldingsfelter {
  fra: string;
  til: string;
  hvem: string;
  hva: string;
  hvor: string;
  naar: string;
  hvorledes: string;
}

export interface Meldingsutkast {
  felter: Meldingsfelter;
  dtgMs: number;
  sone: StridsSone;
  /** Posisjonen meldingen får på kartet i SKADE: SISTE utløsnings sensor. */
  lat?: number;
  lon?: number;
  /** Hva utkastet er bygget av. Vises i skjermen, sendes ikke. */
  grunnlag: string[];
}

/**
 * Maksimallengder fra `StridsmeldingSchema`. De håndheves HER og ikke bare av
 * skjemaet, fordi `CombatReport` tillater 4000 tegn per felt: skrev vi 2000 tegn
 * i HVORLEDES, ville meldingen gått gjennom som CombatReport og blitt AVVIST som
 * Stridsmelding. Da hadde SKADE hatt en melding jegeren ikke kunne lese opp.
 */
export const MAKS = { fra: 120, til: 120, hvem: 400, hva: 600, hvor: 300, naar: 120, hvorledes: 1500 } as const;

/**
 * Kapp en tekst til `maks` tegn og SI at den ble kappet.
 *
 * Stille avkorting er den verste varianten: en melding som mangler den siste
 * advarselen ser komplett ut. Merket «[…KAPPET]» er med store bokstaver fordi det
 * skal fanges av øyet under en opplesning.
 */
export function kapp(tekst: string, maks: number): string {
  if (tekst.length <= maks) return tekst;
  const merke = ' […KAPPET]';
  return `${tekst.slice(0, Math.max(0, maks - merke.length)).trimEnd()}${merke}`;
}

export interface UtkastValg {
  /** Avsender — lagets kallesignal. */
  fra: string;
  /** Mottaker. Standard «SKADE» (hubben). */
  til?: string;
  sone?: StridsSone;
  /** Jegerens egne ord. De står ØVERST i HVORLEDES — se kommentaren der. */
  egneOrd?: string;
  naaMs: number;
}

/**
 * Bygg utkastet fra en analysert kandidat.
 *
 * Rekkefølgen i HVORLEDES er en prioritering under kapping: jegerens egne ord
 * først, deretter forbeholdet om at en UGS ikke gir målidentitet, deretter
 * kontraktens `advarsler`, og til SLUTT den lange `begrunnelse`-utledningen. Blir
 * meldingen for lang, er det utledningen som ryker — den kan etterspørres, mens
 * jegerens vurdering og forbeholdet ikke kan gjenskapes av mottakeren.
 */
export function lagUtkast(
  k: Kandidat,
  sensorer: readonly UgsSensor[],
  valg: UtkastValg,
): Meldingsutkast {
  const sone: StridsSone = valg.sone ?? 'A';
  const kart = new Map(sensorer.map((s) => [s.sensorId, s]));
  const grunnlag: string[] = [];

  const forste = k.hendelser[0];
  const siste = k.hendelser[k.hendelser.length - 1];
  const sisteSensor = kart.get(siste.sensorId) ?? null;
  const forsteSensor = kart.get(forste.sensorId) ?? null;

  // ── HVOR ────────────────────────────────────────────────────────────────
  // Grid for SISTE utløsning, fordi det er der målet sist var kjent — det er
  // spørsmålet mottakeren stiller. Første utløsnings grid står også, fordi de to
  // sammen ER bevegelsen.
  let hvor: string;
  let lat: number | undefined;
  let lon: number | undefined;
  if (sisteSensor) {
    const pos = sensorPos(sisteSensor);
    lat = pos.lat;
    lon = pos.lon;
    const usikker = Math.round(dekningsradiusM(sisteSensor) + posisjonUsikkerhetM(sisteSensor));
    hvor = `MGRS ${latLonToMgrs(pos)} (${sisteSensor.navn}, målet var innenfor ${usikker} m av noden)`;
    grunnlag.push(`HVOR: sensor ${sisteSensor.sensorId} sin posisjon + deteksjonsradius + posisjonsfeil (${usikker} m)`);
    if (forsteSensor && forsteSensor.sensorId !== sisteSensor.sensorId) {
      hvor += `. FØRSTE utløsning MGRS ${latLonToMgrs(sensorPos(forsteSensor))}`;
    }
  } else {
    hvor = 'UKJENT — noden som utløste er IKKE annonsert av broen, så posisjonen finnes ikke';
    grunnlag.push('HVOR: kunne ikke settes — sensoren mangler posisjon');
  }

  // ── HVEM ────────────────────────────────────────────────────────────────
  // «UKJENT» først, alltid. En UGS gir tid og sted, aldri målidentitet, og en
  // melding som åpner med en klasse blir lest som en identifikasjon.
  const antall = k.sekvens
    ? formaterAntall(k.sekvens.antallPasserendeAntatt, k.sekvens.antallPasserendeMin, k.sekvens.antallPasserendeMaks)
    : 'kan ikke anslås fra én utløsning';
  const klasse = k.beste
    ? `Sterkeste holdepunkt: ${MALKLASSE_NAVN[k.beste.klasse.klasse]} (konfidens ${k.beste.klasse.konfidens.toFixed(2)}, grunnlag: ${GRUNNLAG_NAVN[k.beste.klasse.grunnlag]})`
    : 'Ingen klassifisering';
  const hvem = kapp(`UKJENT. Antall passerende: ${antall}. ${klasse}.`, MAKS.hvem);
  grunnlag.push(`HVEM: antallPasserende og sterkeste enkeltklassifisering fra kontrakten (ingen sammenslåing av konfidens)`);

  // ── HVA ─────────────────────────────────────────────────────────────────
  const noder = new Set(k.sensorIder).size;
  const deler: string[] = [
    `${k.hendelser.length} UGS-utløsning${k.hendelser.length === 1 ? '' : 'er'} på ${noder} node${noder === 1 ? '' : 'r'}`,
  ];
  if (k.sekvens) {
    deler.push(`over ${formaterVarighet(k.sekvens.varighetS)}`);
    const r = retningsutsagn(k.sekvens);
    deler.push(r.slag === 'bestemt' ? r.tekst.replace(/\.$/, '') : r.tekst.replace(/\.$/, ''));
    const f = fartsutsagn(k.sekvens.nettoFartMinMs, k.sekvens.nettoFartMaksMs);
    deler.push(f.slag === 'ingen' ? f.tekst : `Netto fart ${f.tekst}`);
    grunnlag.push('HVA: retningsutsagn + nettoFartMin/Maks fra kontrakten — fart som INTERVALL, aldri som ett tall');
  } else {
    deler.push(k.avslag ?? 'kan ikke analyseres som sekvens');
    grunnlag.push('HVA: kontrakten nektet å analysere — årsaken er tatt inn ORDRETT');
  }
  const hva = kapp(`${deler.join('. ')}.`, MAKS.hva);

  // ── NÅR ─────────────────────────────────────────────────────────────────
  const dtgFra = buildDtg(Math.round(k.fraMs), sone);
  const dtgTil = buildDtg(Math.round(k.tilMs), sone);
  const naar = kapp(dtgFra === dtgTil ? dtgFra : `${dtgFra} til ${dtgTil}`, MAKS.naar);
  grunnlag.push('NÅR: DTG for første og siste utløsning (nodenes egne klokker — se HVORLEDES for slark)');

  // ── HVORLEDES ───────────────────────────────────────────────────────────
  const linjer: string[] = [];
  if (valg.egneOrd && valg.egneOrd.trim().length > 0) linjer.push(valg.egneOrd.trim());
  linjer.push(
    'FORBEHOLD: en UGS gir tid og sted, ALDRI målidentitet. Samme datasett er like forenlig med flere uavhengige mål som med ett i bevegelse.',
  );
  if (k.sekvens) {
    for (const a of k.sekvens.advarsler) linjer.push(`ADVARSEL: ${a}`);
    for (const b of k.sekvens.begrunnelse) linjer.push(b);
  }
  const hvorledes = kapp(linjer.join(' | '), MAKS.hvorledes);
  grunnlag.push('HVORLEDES: jegerens egne ord først, så forbeholdet, så kontraktens advarsler og begrunnelse ORDRETT');

  return {
    felter: {
      fra: kapp(valg.fra, MAKS.fra),
      til: kapp(valg.til ?? 'SKADE', MAKS.til),
      hvem,
      hva,
      hvor: kapp(hvor, MAKS.hvor),
      naar,
      hvorledes,
    },
    dtgMs: Math.round(k.tilMs),
    sone,
    ...(lat !== undefined ? { lat } : {}),
    ...(lon !== undefined ? { lon } : {}),
    grunnlag,
  };
}

/**
 * Utkast → `Stridsmelding`, validert med kontraktens skjema.
 *
 * `null` ved ugyldig, aldri kast — samme regel som `parseUgsSensor`. Returnerer
 * den `null`, er det en FEIL I ULL (for lang tekst, manglende felt), og skjermen
 * skal si at meldingen ikke kan bygges framfor å sende noe halvt.
 */
export function tilStridsmelding(u: Meldingsutkast, id: string, av: string, naaMs: number): Stridsmelding | null {
  const p = StridsmeldingSchema.safeParse({
    id,
    dtgMs: u.dtgMs,
    sone: u.sone,
    ...u.felter,
    ...(u.lat !== undefined ? { lat: u.lat } : {}),
    ...(u.lon !== undefined ? { lon: u.lon } : {}),
    createdBy: av,
    tsMs: naaMs,
  });
  return p.success ? p.data : null;
}

/** Åtte linjer klartekst, klar til å leses opp over samband. */
export function radiotekst(m: Stridsmelding): string {
  return formatStridsmelding(m);
}

export type ReportForsok =
  | { ok: true; rapport: CombatReport }
  | { ok: false; grunn: string };

/**
 * Utkast → `CombatReport` (`type: 'MELDING'`), validert med kontraktens skjema.
 *
 * `lat`/`lon` er PÅKREVD i skjemaet, og det er riktig: en melding uten posisjon
 * kan ikke tegnes i COP, og en melding SKADE ikke kan tegne er en melding ingen
 * ser. Mangler posisjonen, nekter vi — og sier hvorfor, slik at jegeren kan lese
 * klarteksten opp over sambandet i stedet.
 */
export function tilCombatReport(
  u: Meldingsutkast,
  id: string,
  av: string,
  naaMs: number,
): ReportForsok {
  if (u.lat === undefined || u.lon === undefined) {
    return {
      ok: false,
      grunn:
        'Meldingen mangler posisjon, og SKADE krever den for å kunne tegne meldingen i kartet. Årsaken står i HVOR-feltet. '
        + 'Klarteksten kan fortsatt leses opp over sambandet.',
    };
  }
  const p = CombatReportSchema.safeParse({
    id,
    type: 'MELDING',
    lat: u.lat,
    lon: u.lon,
    tsMs: naaMs,
    author: av,
    fields: { ...u.felter },
    status: 'ny',
    updatedMs: naaMs,
    thread: [],
  });
  if (!p.success) {
    return {
      ok: false,
      grunn: `Meldingen ble avvist av kontraktens skjema: ${p.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`,
    };
  }
  return { ok: true, rapport: p.data };
}
